/**
 * useDashboardWS — WebSocket hook for real-time dashboard events.
 *
 * Connects to the dashboard WebSocket endpoint, authenticates with token,
 * dispatches events to useDashboardStore, auto-reconnects with exponential backoff.
 *
 * Also handles sandbox-specific events (sandbox.*) by dispatching to useSandboxStore.
 */
import { useEffect, useRef, useCallback, useState } from "react";
import { useDashboardStore } from "@/stores/useDashboardStore";
import { useSandboxStore } from "@/stores/useSandboxStore";
import { useAuthStore } from "@/stores/useAuthStore";
import type { DashboardEvent } from "@ajnt/shared";

const WS_URL_PROD = "wss://api.ajnt.ai/api/dashboard/ws";
const WS_URL_DEV = "ws://localhost:8787/api/dashboard/ws";

const MAX_BACKOFF = 30_000;
const INITIAL_BACKOFF = 1_000;

function getWsUrl(): string {
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    return WS_URL_DEV;
  }
  return WS_URL_PROD;
}

interface UseDashboardWSReturn {
  isConnected: boolean;
  reconnect(): void;
  disconnect(): void;
}

export function useDashboardWS(): UseDashboardWSReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(INITIAL_BACKOFF);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const [isConnected, setIsConnected] = useState(false);

  const addEvent = useDashboardStore((s) => s.addEvent);
  const setConnectionStatus = useDashboardStore((s) => s.setConnectionStatus);
  const token = useAuthStore((s) => s.session?.token ?? null);

  const cleanupWs = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    cleanupWs();

    setConnectionStatus("connecting");

    try {
      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        // Send auth token as first message
        if (token) {
          ws.send(JSON.stringify({ type: "auth", token }));
        }
        backoffRef.current = INITIAL_BACKOFF;
        setIsConnected(true);
        setConnectionStatus("connected");
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data as string) as DashboardEvent;
          if (data && data.type && data.timestamp) {
            // Route sandbox events to sandbox store
            if (typeof data.type === "string" && data.type.startsWith("sandbox.")) {
              useSandboxStore.getState().handleSandboxEvent({
                type: data.type,
                orgId: data.orgId,
                timestamp: data.timestamp,
                payload: data.payload as Record<string, unknown> | undefined,
              });
            }

            // Always dispatch to dashboard store for event feed
            addEvent(data);
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        setIsConnected(false);
        setConnectionStatus("disconnected");
        scheduleReconnect();
      };

      ws.onerror = () => {
        if (!mountedRef.current) return;
        // onerror is always followed by onclose, so we just let onclose handle reconnect
      };
    } catch {
      // Network error — schedule reconnect
      setIsConnected(false);
      setConnectionStatus("disconnected");
      scheduleReconnect();
    }
  }, [token, addEvent, setConnectionStatus, cleanupWs]);

  function scheduleReconnect() {
    if (!mountedRef.current) return;
    const delay = backoffRef.current;
    backoffRef.current = Math.min(delay * 2, MAX_BACKOFF);
    reconnectTimerRef.current = setTimeout(() => {
      if (mountedRef.current) connect();
    }, delay);
  }

  const reconnect = useCallback(() => {
    backoffRef.current = INITIAL_BACKOFF;
    connect();
  }, [connect]);

  const disconnect = useCallback(() => {
    cleanupWs();
    setIsConnected(false);
    setConnectionStatus("disconnected");
  }, [cleanupWs, setConnectionStatus]);

  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      cleanupWs();
    };
  }, [connect, cleanupWs]);

  return { isConnected, reconnect, disconnect };
}
